// From:
// https://docs.aws.amazon.com/polly/latest/dg/supportedtags.html
const VALID_SSML_TAGS = ['speak', 'break', 'lang', 'mark', 'p', 'phoneme', 'prosody', 's', 'say-as', 'sub', 'w'];

export function validateSSML(content: string): {
  isValid: boolean;
  errors: string[];
} {
  const errors: string[] = [];

  if (!content || content.trim() === '') {
    return { isValid: true, errors: [] };
  }

  if (!content.startsWith('<speak>')) {
    errors.push('Content must start with <speak> tag');
  }

  if (!content.endsWith('</speak>')) {
    errors.push('Content must end with </speak> tag');
  }

  const tagRegex = /<\/?([a-zA-Z][\w:-]*)[^>]*\/?>/g;
  const tagStack: Array<{ name: string; pos: number }> = [];
  const foundTags = new Set<string>();
  let match;

  while ((match = tagRegex.exec(content)) !== null) {
    const fullTag = match[0];
    const tagName = match[1];
    const isSelfClosing = fullTag.endsWith('/>');
    const isClosing = fullTag.startsWith('</');

    foundTags.add(tagName);

    if (!VALID_SSML_TAGS.includes(tagName)) {
      errors.push(`Invalid SSML tag: <${tagName}>`);
    }

    if (isSelfClosing) {
      continue;
    } else if (isClosing) {
      if (tagStack.length === 0) {
        errors.push(`Closing tag </${tagName}> has no matching opening tag`);
      } else {
        const lastOpen = tagStack[tagStack.length - 1];
        if (lastOpen.name === tagName) {
          tagStack.pop();
        } else {
          errors.push(`Tag mismatch: expected </${lastOpen.name}> but found </${tagName}>`);
          tagStack.pop();
        }
      }
    } else {
      tagStack.push({ name: tagName, pos: match.index });
    }
  }

  if (tagStack.length > 0) {
    tagStack.forEach((tag) => {
      errors.push(`Unclosed tag: <${tag.name}>`);
    });
  }

  const textContent = content.replace(/<[^>]+>/g, '');
  const unescapedChars = textContent.match(/[<>&](?!(?:lt|gt|amp|quot|apos);)/g);
  if (unescapedChars) {
    errors.push('Special characters must be escaped (&lt; &gt; &amp; &quot; &apos;)');
  }

  return {
    isValid: errors.length === 0,
    errors
  };
}
